import React, { useState, useContext, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import UserContext from "../context/UserContext";
import { api } from "../utils/apiHelper";
import "../styles/DigiStore.css";

const DigiStore = () => {
  const { authUser } = useContext(UserContext);
  const { projectId: paramProjectId } = useParams();
  const projectId = paramProjectId || uuidv4();

  const [projectTitle, setProjectTitle] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [accessType, setAccessType] = useState("private");
  const [licenseType, setLicenseType] = useState("free");
  const [uploading, setUploading] = useState(false);

  // Dropzone for images
  const onImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
    setImageFile(file);
  }, []);
  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps } = useDropzone({
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/gif": [".gif"] },
    onDrop: onImageDrop,
    multiple: false,
  });

  // Dropzone for audio files
  const onAudioDrop = useCallback((acceptedFiles) => {
    setAudioFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.map(file => ({
        id: uuidv4(),
        title: file.name.replace(/\.[^/.]+$/, ""), // Default title = file name (no extension)
        file,
        fileId: null,
        url: null,
      }))
    ]);
  }, []);
  const { getRootProps: getAudioRootProps, getInputProps: getAudioInputProps } = useDropzone({
    accept: { "audio/*": [".mp3", ".m4a", ".wav", ".aac"] },
    onDrop: onAudioDrop,
    multiple: true,
  });

  const extractMetadata = async (fileKey) => {
    try {
      const response = await api("/extract-audio-metadata", "POST", { fileKey });
      return response.metadata;
    } catch (error) {
      console.error("Error extracting metadata:", error);
      return null;
    }
  };

  const removeAudioFile = (id) => {
    setAudioFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
  };

  const handleAudioTitleChange = (index, title) => {
    setAudioFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles[index] = { ...updatedFiles[index], title };
      return updatedFiles;
    });
  };

  const uploadToS3 = async (file, folder) => {
    try {
      const userId = authUser?._id;
      if (!userId) throw new Error("User not authenticated");

      const response = await api("/digi-signed-url", "POST", {
        fileName: file.name,
        fileType: file.type,
        accessType,
        userId,
        projectId,
        folder
      });

      if (!response.url || !response.key) return null;

      await fetch(response.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      return response.key;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploading(true);
    const userId = authUser?._id;

    if (!userId) {
      console.error("User not authenticated. Cannot submit.");
      setUploading(false);
      return;
    }

    let imageKey = null;
    if (imageFile) {
      const folder = `users/${userId}/digiStore/${projectId}/images`;
      imageKey = await uploadToS3(imageFile, folder);
    }

    const uploadedAudioFiles = await Promise.all(
      audioFiles.map(async (file) => {
        if (!file.file) return file;
        const folder = `users/${userId}/digiStore/${projectId}/audioFiles`;
        const fileKey = await uploadToS3(file.file, folder);
        const metadata = await extractMetadata(fileKey);

        return {
          ...file,
          fileId: fileKey,
          metadata,
          url: `https://editfarm.s3.us-east-2.amazonaws.com/${fileKey}`,
        };
      })
    );

    const validAudioFiles = uploadedAudioFiles.filter((file) => file.fileId);

    if (!imageKey && validAudioFiles.length === 0) {
      console.error("No files uploaded to S3. Aborting submission.");
      setUploading(false);
      return;
    }

    const fileKeys = validAudioFiles.map((file) => ({
      key: file.fileId,
      fileName: file.file.name,
      metadata: file.metadata,
      url: file.url,
    }));

    const payload = {
        userId,
        projectId,
        projectTitle,
        description,
        price,
        accessType,
        licenseType,
        image: imageKey,
        fileKeys,
        quantity: quantity !== "" ? Number(quantity) : undefined
      };

    try {
      const response = await api("/digi-upload-product", "POST", payload);

      if (response.error) {
        console.error("Submission failed:", response.error);
        setUploading(false);
        return;
      }

      setAudioFiles([]);
      setImage(null);
      setImageFile(null);
      setProjectTitle("");
      setDescription("");
      setPrice("");
      setAccessType("private");
      setLicenseType("free");
      setQuantity("");
      window.alert("Upload successful!");
      console.log("Submission successful.");
    } catch (error) {
     window.alert("An unexpected error occurred. Please try again.");
      console.error("Error submitting product:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
   <div className="digi-store-wrapper">
  <h2 className="digi-store-title">Create Your Product</h2>

  <form onSubmit={handleSubmit}>
    <div className="digi-form-group">
      <label className="digi-form-label">Product Title</label>
      <input
        type="text"
        className="digi-input"
        value={projectTitle}
        onChange={(e) => setProjectTitle(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Price ($)</label>
      <input
        type="number"
        className="digi-input"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Description</label>
      <textarea
        className="digi-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">Quantity (optional)</label>
      <input
        type="number"
        min="0"
        max="1000"
        className="digi-input"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        placeholder="optional"
      />
    </div>

    <div className="digi-form-group">
      <label className="digi-form-label">License Type</label>
      <select
        className="digi-input"
        value={licenseType}
        onChange={(e) => setLicenseType(e.target.value)}
      >
        <option value="free">Free License (Non-Commercial Use)</option>
        <option value="non-exclusive">Non-Exclusive License</option>
        <option value="exclusive">Exclusive License</option>
      </select>
    </div>

    <h3>Upload Cover Image</h3>
    <div {...getImageRootProps()} className="digi-dropzone">
      <input {...getImageInputProps()} />
      <p>Drag & drop an image or tap to select</p>
    </div>
    {image && <img src={image} alt="Preview" className="digi-preview-image" />}

    <h3>Audio Files</h3>
    <div {...getAudioRootProps()} className="digi-dropzone">
      <input {...getAudioInputProps()} />
      <p>Drag & drop audio files or tap to select</p>
    </div>
    <div className="digi-audio-list">
      {audioFiles.map((audio, index) => (
        <div key={audio.id} className="digi-audio-item">
          <input
            type="text"
            className="digi-input"
            value={audio.title}
            onChange={(e) => handleAudioTitleChange(index, e.target.value)}
          />
          <span>{audio.file?.name}</span>
          <button
            type="button"
            className="digi-remove-button"
            onClick={() => removeAudioFile(audio.id)}
          >
            Remove
          </button>
        </div>
      ))}
    </div>

    <button type="submit" className="digi-button" disabled={uploading}>
      {uploading ? "Uploading..." : "Submit Product"}
    </button>
  </form>
</div>
  );
};

export default DigiStore;
